import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, MessageCircle, MapPin, Eye } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { getMatches, getLikesReceived, getViewsReceived } from '../services/api';
import { useDefaultAvatar } from '../services/defaultAvatar';

const MatchesPage = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [likesCount, setLikesCount] = useState(0);
  const [viewsCount, setViewsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMatches();
    getLikesReceived({ limit: 1 }).then(r => setLikesCount(r.data.total)).catch(() => {});
    getViewsReceived({ limit: 1 }).then(r => setViewsCount(r.data.total)).catch(() => {});
  }, []);

  const loadMatches = async () => {
    try {
      const response = await getMatches();
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to load matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const defaultPhoto = useDefaultAvatar() || 'https://images.unsplash.com/photo-1650702970095-f5b8ebeebce5?w=400';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center pb-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#009E60] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24" data-testid="matches-page">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 py-3">
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
            Your Matches
          </h1>
          <p className="text-sm text-gray-500">{matches.length} connections</p>
        </div>
      </div>

      {/* Content */}
      <div className="pt-20 px-4">
        <div className="max-w-lg mx-auto">
          {/* Discovery quick-links */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              onClick={() => navigate('/likes')}
              className="bg-gradient-to-br from-pink-500 to-rose-500 text-white rounded-2xl p-4 text-left shadow-sm hover:shadow-md transition-shadow"
              data-testid="who-liked-me-card"
            >
              <div className="flex items-center gap-2 mb-1">
                <Heart className="w-5 h-5" fill="white" />
                <span className="font-semibold">Who Liked You</span>
              </div>
              <div className="text-xs opacity-90">{likesCount} {likesCount === 1 ? 'like' : 'likes'}</div>
            </button>
            <button
              onClick={() => navigate('/views')}
              className="bg-gradient-to-br from-indigo-500 to-blue-500 text-white rounded-2xl p-4 text-left shadow-sm hover:shadow-md transition-shadow"
              data-testid="who-viewed-me-card"
            >
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-5 h-5" />
                <span className="font-semibold">Who Viewed You</span>
              </div>
              <div className="text-xs opacity-90">{viewsCount} recent</div>
            </button>
          </div>

          {matches.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-10 h-10 text-gray-300" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                No matches yet
              </h3>
              <p className="text-gray-500 mb-4">
                Keep swiping to find your perfect match!
              </p>
              <button
                onClick={() => navigate('/discover')}
                className="text-[#009E60] font-semibold hover:underline"
              >
                Start Discovering
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {matches.map((match, index) => (
                <motion.div
                  key={match.match_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/messages/${match.match_id}`)}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  data-testid={`match-${match.match_id}`}
                >
                  <div className="relative aspect-[3/4]">
                    <img
                      src={match.user?.photos?.[0] || defaultPhoto}
                      alt={match.user?.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="text-white font-semibold truncate">
                        {match.user?.name}, {match.user?.age || '??'}
                      </h3>
                      {match.user?.location && (
                        <div className="flex items-center gap-1 text-white/80 text-sm">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{match.user.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-3">
                    <button
                      className="w-full flex items-center justify-center gap-2 py-2 bg-[#009E60]/10 text-[#009E60] rounded-full text-sm font-medium hover:bg-[#009E60]/20 transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Message
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default MatchesPage;
